import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';

const contractsSearchSchema = z.object({
    create: z.string().optional(),
    employee_id: z.string().optional(),
})

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, PencilIcon, DocumentTextIcon, TrashIcon } from '@heroicons/react/24/outline';
import PaginationFooter from '@/components/PaginationFooter';
import { formatCurrency, getCurrencySymbol } from '@/utils/formatters';

interface Employee {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
}

interface CostCenter {
    id: string;
    name: string;
    address: string | null;
    is_active: boolean;
}

interface Company {
    id: string;
    name: string;
    tax_id: string | null;
    is_active: boolean;
}

interface TimesheetJob {
    id: string;
    title: string;
    code?: string;
    levels?: TimesheetJobLevel[];
}

interface TimesheetJobLevel {
    id: string;
    job_id: string;
    level_name: 'Junior' | 'Experienced' | 'Senior';
    hourly_rate: number;
    is_default: boolean;
}

interface ContractJobAssignment {
    id?: string;
    job_id: string;
    job_level_id: string;
    cost_center_id?: string | null;
}

interface EmploymentContract {
    id: string;
    employee_id: string;
    company_id: string;
    taxation_type: 'full_time_employee' | 'daily_employee' | 'student';
    default_cost_center_id: string | null;
    start_date: string;
    end_date: string | null;
    currency: string;
    monthly_salary?: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    employee?: Employee;
    default_cost_center?: CostCenter;
    job_assignments: ContractJobAssignment[];
}

interface ContractFormData {
    employee_id: string;
    company_id: string;
    taxation_type: 'full_time_employee' | 'daily_employee' | 'student';
    default_cost_center_id: string;
    start_date: string;
    end_date: string;
    monthly_salary: string;
    is_active: boolean;
}


interface JobAssignmentFormData {
    job_id: string;
    job_level_id: string;
    cost_center_id: string;
}

export default function ContractsPage() {
    const { t } = useI18n();

    // State
    const [contracts, setContracts] = useState<EmploymentContract[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [jobs, setJobs] = useState<TimesheetJob[]>([]);
    const [baseCurrency, setBaseCurrency] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    const search = Route.useSearch();

    // Dialog state
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [editingContract, setEditingContract] = useState<EmploymentContract | null>(null);
    const [formData, setFormData] = useState<ContractFormData>({
        employee_id: '',
        company_id: '',
        taxation_type: 'full_time_employee',
        default_cost_center_id: '',
        start_date: '',
        end_date: '',
        monthly_salary: '',
        is_active: true
    });
    const [jobAssignments, setJobAssignments] = useState<JobAssignmentFormData[]>([]);
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => {
        if (search?.create && search?.employee_id) {
            setFormData((prev) => ({
                ...prev,
                employee_id: search.employee_id!,
                start_date: new Date().toISOString().split('T')[0],
            }))
            setShowCreateDialog(true)
        }
    }, [search])


    // Fetch data
    useEffect(() => {
        fetchContracts();
        fetchEmployees();
        fetchCostCenters();
        fetchCompanies();
        fetchJobs();
        fetchBaseCurrency();
    }, [pagination.page, pagination.limit]);

    const fetchContracts = async () => {
        try {
            setLoading(true);
            const skip = (pagination.page - 1) * pagination.limit;
            const params = new URLSearchParams({
                skip: skip.toString(),
                limit: pagination.limit.toString()
            });

            const response = await apiFetch(`/api/people/contracts/?${params}`);
            if (response.ok) {
                const data = await response.json();
                setContracts(data.contracts);
                setPagination(prev => ({
                    ...prev,
                    total: data.total,
                    pages: data.pagination.pages
                }));
            } else {
                throw new Error('Failed to fetch contracts');
            }
        } catch (err) {
            console.error('Error fetching contracts:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch contracts');
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            const response = await apiFetch('/api/people/employees/?limit=100');
            if (response.ok) {
                const data = await response.json();
                setEmployees(data.employees || []);
            }
        } catch (err) {
            console.error('Error fetching employees:', err);
        }
    };

    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/?limit=100');
            if (response.ok) {
                const data = await response.json();
                // The API returns a direct array, not wrapped in a property
                setCostCenters(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching cost centers:', err);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await apiFetch('/api/companies/?limit=100');
            if (response.ok) {
                const data = await response.json();
                setCompanies(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching companies:', err);
        }
    };

    const fetchJobs = async () => {
        try {
            const response = await apiFetch('/api/timesheet/jobs?per_page=100&include_deleted=false');
            if (response.ok) {
                const data = await response.json();
                setJobs(data.jobs || []);
            }
        } catch (err) {
            console.error('Error fetching jobs:', err);
        }
    };

    const fetchBaseCurrency = async () => {
        try {
            const response = await apiFetch('/api/currencies/base-currency');
            if (response.ok) {
                const data = await response.json();
                setBaseCurrency(data.base_currency);
            } else {
                // Fallback to a reasonable default if API fails
                setBaseCurrency('USD');
            }
        } catch (err) {
            console.error('Error fetching base currency:', err);
            // Fallback to a reasonable default if API fails
            setBaseCurrency('USD');
        }
    };

    const resetForm = () => {
        setFormData({
            employee_id: '',
            company_id: '',
            taxation_type: 'full_time_employee',
            default_cost_center_id: '',
            start_date: '',
            end_date: '',
            monthly_salary: '',
            is_active: true
        });
        setJobAssignments([]);
    };

    const openCreateDialog = () => {
        resetForm();
        setEditingContract(null);
        setShowCreateDialog(true);
    };

    const openEditDialog = (contract: EmploymentContract) => {
        setFormData({
            employee_id: contract.employee_id,
            company_id: contract.company_id,
            taxation_type: contract.taxation_type,
            default_cost_center_id: contract.default_cost_center_id || '',
            start_date: contract.start_date.split('T')[0],
            end_date: contract.end_date ? contract.end_date.split('T')[0] : '',
            monthly_salary: contract.monthly_salary ? contract.monthly_salary.toString() : '',
            is_active: contract.is_active
        });
        setJobAssignments(
            contract.job_assignments.map(ja => ({
                job_id: ja.job_id,
                job_level_id: ja.job_level_id,
                cost_center_id: ja.cost_center_id || ''
            }))
        );
        setEditingContract(contract);
        setShowCreateDialog(true);
    };

    // Job assignment handlers
    const addJobAssignment = () => {
        setJobAssignments([...jobAssignments, {
            job_id: '',
            job_level_id: '',
            cost_center_id: ''
        }]);
    };

    const removeJobAssignment = (index: number) => {
        setJobAssignments(jobAssignments.filter((_, i) => i !== index));
    };

    const updateJobAssignment = (index: number, field: keyof JobAssignmentFormData, value: string) => {
        const updated = [...jobAssignments];
        updated[index] = { ...updated[index], [field]: value };

        // If job changed, reset job_level_id
        if (field === 'job_id') {
            updated[index].job_level_id = '';
        }

        setJobAssignments(updated);
    };

    const getAvailableLevelsForJob = (jobId: string): TimesheetJobLevel[] => {
        const job = jobs.find(j => j.id === jobId);
        return job?.levels || [];
    };

    const validateForm = (): string | null => {
        if (!formData.employee_id) return t('contracts.validation.selectEmployee', 'people');
        if (!formData.start_date) return t('contracts.validation.selectStartDate', 'people');

        // Job assignments are only required for active contracts
        if (formData.is_active && jobAssignments.length === 0) {
            return t('contracts.validation.addJobAssignment', 'people');
        }

        for (let i = 0; i < jobAssignments.length; i++) {
            const assignment = jobAssignments[i];
            if (!assignment.job_id) return t('contracts.validation.jobAssignmentSelectJob', 'people', { number: i + 1 });
            if (!assignment.job_level_id) return t('contracts.validation.jobAssignmentSelectLevel', 'people', { number: i + 1 });

            // Check for duplicate jobs
            const duplicateJob = jobAssignments.find((ja, idx) =>
                idx !== i && ja.job_id === assignment.job_id
            );
            if (duplicateJob) {
                const job = jobs.find(j => j.id === assignment.job_id);
                return t('contracts.validation.duplicateJob', 'people', { jobTitle: job?.title });
            }
        }

        return null;
    };

    const handleSubmit = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const submitData: any = {
                employee_id: formData.employee_id,
                company_id: formData.company_id,
                taxation_type: formData.taxation_type,
                default_cost_center_id: formData.default_cost_center_id || null,
                start_date: new Date(formData.start_date).toISOString(),
                end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
                is_active: formData.is_active,
                job_assignments: jobAssignments.map(ja => ({
                    job_id: ja.job_id,
                    job_level_id: ja.job_level_id,
                    cost_center_id: ja.cost_center_id || null
                }))
            };

            // Only include monthly_salary for full_time_employee
            if (formData.taxation_type === 'full_time_employee' && formData.monthly_salary) {
                submitData.monthly_salary = parseFloat(formData.monthly_salary);
            }

            const url = editingContract
                ? `/api/people/contracts/${editingContract.id}`
                : '/api/people/contracts/';
            const method = editingContract ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submitData)
            });

            if (response.ok) {
                await fetchContracts();
                setShowCreateDialog(false);
                resetForm();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Failed to ${editingContract ? 'update' : 'create'} contract`);
            }
        } catch (err) {
            console.error('Error submitting contract:', err);
            setError(err instanceof Error ? err.message : `Failed to ${editingContract ? 'update' : 'create'} contract`);
        } finally {
            setSubmitting(false);
        }
    };

    const formatTaxationType = (type: string) => {
        if (type === 'full_time_employee') return t('contracts.taxationType.fullTime', 'people');
        if (type === 'daily_employee') return t('contracts.taxationType.daily', 'people');
        if (type === 'student') return t('contracts.taxationType.student', 'people');
        return type;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getEmployeeName = (employeeId: string) => {
        const employee = employees.find(emp => emp.id === employeeId);
        return employee ? `${employee.first_name} ${employee.last_name}` : t('contracts.unknownEmployee', 'people');
    };

    const getCostCenterName = (costCenterId: string | null) => {
        if (!costCenterId) return t('contracts.noCostCenter', 'people');
        const costCenter = costCenters.find(cc => cc.id === costCenterId);
        return costCenter ? costCenter.name : t('contracts.unknownCostCenter', 'people');
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('contracts.title', 'people')}</h1>
                        <p className="text-muted-foreground">
                            {t('contracts.subtitle', 'people')}
                        </p>
                    </div>
                    <Button onClick={openCreateDialog}>
                        <PlusIcon className="w-4 h-4 mr-2" />
                        {t('actions.addNew', 'common')} {t('contracts.single_contract', 'people')}
                    </Button>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                {/* Table */}
                <div className="border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('common.employee', 'people')}</TableHead>
                                <TableHead>{t('contracts.jobAssignments', 'people')}</TableHead>
                                <TableHead>{t('contracts.taxationType.title', 'people')}</TableHead>
                                <TableHead>{t('contracts.defaultCostCenter', 'people')}</TableHead>
                                <TableHead>{t('contracts.startDate', 'people')}</TableHead>
                                <TableHead>{t('contracts.endDate', 'people')}</TableHead>
                                <TableHead>{t('common.status', 'people')}</TableHead>
                                <TableHead className="w-[100px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                    </TableRow>
                                ))
                            ) : contracts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8">
                                        <div className="flex flex-col items-center gap-2">
                                            <DocumentTextIcon className="h-12 w-12 text-gray-400" />
                                            <p className="text-muted-foreground">{t('contracts.noContractsFound', 'people')}</p>
                                            <Button variant="outline" onClick={openCreateDialog}>
                                                {t('contracts.createFirstContract', 'people')}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                contracts.map((contract) => (
                                    <TableRow
                                        key={contract.id}
                                        className={`${!contract.is_active ? 'opacity-60 bg-muted/20' : ''} cursor-pointer hover:bg-muted/50`}
                                        onClick={() => openEditDialog(contract)}
                                    >
                                        <TableCell className="font-medium">
                                            {contract.employee ?
                                                `${contract.employee.first_name} ${contract.employee.last_name}` :
                                                getEmployeeName(contract.employee_id)
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {contract.job_assignments.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {contract.job_assignments.map((assignment, idx) => {
                                                        const job = jobs.find(j => j.id === assignment.job_id);
                                                        const level = job?.levels?.find(l => l.id === assignment.job_level_id);
                                                        return (
                                                            <Badge key={idx} variant="outline" className="text-xs">
                                                                {job?.title || t('contracts.unknown', 'people')} - {level?.level_name || t('contracts.unknown', 'people')}
                                                            </Badge>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground text-sm">{t('contracts.noAssignments', 'people')}</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {formatTaxationType(contract.taxation_type)}
                                        </TableCell>
                                        <TableCell>
                                            {getCostCenterName(contract.default_cost_center_id)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(contract.start_date)}
                                        </TableCell>
                                        <TableCell>
                                            {contract.end_date ? formatDate(contract.end_date) : t('contracts.openEnded', 'people')}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={contract.is_active ? "default" : "secondary"}>
                                                {contract.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openEditDialog(contract);
                                                }}
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination */}
                <PaginationFooter
                    pagination={pagination}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                    onPageSizeChange={(pageSize) => setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }))}
                />
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={(open) => {
                if (!open) {
                    setShowCreateDialog(false);
                    resetForm();
                    setError(null);
                }
            }}>
                <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingContract ? t('contracts.editContract', 'people') : t('contracts.createContract', 'people')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6">
                        {/* Error Display */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                                {error}
                            </div>
                        )}

                        {/* Basic Contract Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="employee_id">{t('common.employee', 'people')}</Label>
                                <Select
                                    value={formData.employee_id}
                                    onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                                    disabled={!!editingContract}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('contracts.selectEmployee', 'people')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {employees.map((employee) => (
                                            <SelectItem key={employee.id} value={employee.id}>
                                                {employee.first_name} {employee.last_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="company_id">{t('contracts.company', 'people')}</Label>
                                <Select
                                    value={formData.company_id}
                                    onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('contracts.selectCompany', 'people')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {companies.map((company) => (
                                            <SelectItem key={company.id} value={company.id}>
                                                {company.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="taxation_type">{t('contracts.taxationType.title', 'people')}</Label>
                                <Select
                                    value={formData.taxation_type}
                                    onValueChange={(value: 'full_time_employee' | 'daily_employee' | 'student') => {
                                        setFormData({ ...formData, taxation_type: value, monthly_salary: '' });
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="full_time_employee">{t('contracts.taxationType.fullTime', 'people')}</SelectItem>
                                        <SelectItem value="daily_employee">{t('contracts.taxationType.daily', 'people')}</SelectItem>
                                        <SelectItem value="student">{t('contracts.taxationType.student', 'people')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>


                            {/* Monthly Salxatioield - only for Full Time employees */}
                            {formData.taxation_type === 'full_time_employee' && (
                                <div>
                                    <Label htmlFor="monthly_salary">Monthly Salary ({baseCurrency})</Label>
                                    <Input
                                        id="monthly_salary"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.monthly_salary}
                                        onChange={(e) => setFormData({ ...formData, monthly_salary: e.target.value })}
                                        placeholder="Gross monthly salary"
                                    />
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Net transfer (after taxes): {formData.monthly_salary ? formatCurrency(parseFloat(formData.monthly_salary) * 0.665, getCurrencySymbol(baseCurrency)) : '-'}
                                    </p>
                                </div>
                            )}

                            <div>
                                <Label htmlFor="default_cost_center_id">{t('contracts.defaultCostCenter', 'people')}</Label>
                                <Select
                                    value={formData.default_cost_center_id || "none"}
                                    onValueChange={(value) =>
                                        setFormData({ ...formData, default_cost_center_id: value === "none" ? "" : value })
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('contracts.selectCostCenter', 'people')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">{t('contracts.noDefaultCostCenter', 'people')}</SelectItem>
                                        {costCenters.map((costCenter) => (
                                            <SelectItem key={costCenter.id} value={costCenter.id}>
                                                {costCenter.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="start_date">{t('contracts.startDate', 'people')}</Label>
                                <Input
                                    id="start_date"
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="end_date">{t('contracts.endDate', 'people')}</Label>
                                <Input
                                    id="end_date"
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                />
                            </div>

                            <div className="flex items-center space-x-2 pt-6">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="rounded"
                                />
                                <Label htmlFor="is_active">{t('status.active', 'common')}</Label>
                            </div>
                        </div>

                        {/* Job Assignments Section */}
                        <div className="border-t pt-4">
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold">{t('contracts.jobAssignments', 'people')}</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {t('contracts.jobAssignmentsDescription', 'people')}
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={addJobAssignment}
                                >
                                    <PlusIcon className="w-4 h-4 mr-1" />
                                    {t('contracts.addJob', 'people')}
                                </Button>
                            </div>

                            {jobAssignments.length === 0 ? (
                                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                                    <p className="text-muted-foreground mb-2">{t('contracts.noJobAssignmentsYet', 'people')}</p>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addJobAssignment}
                                    >
                                        <PlusIcon className="w-4 h-4 mr-1" />
                                        {t('contracts.addFirstJob', 'people')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {jobAssignments.map((assignment, index) => {
                                        const availableLevels = getAvailableLevelsForJob(assignment.job_id);

                                        return (
                                            <div key={index} className="flex gap-2 items-start p-3 border rounded-lg bg-muted/20">
                                                <div className="flex-1 grid grid-cols-3 gap-2">
                                                    <div>
                                                        <Label className="text-xs">{t('contracts.job', 'people')}</Label>
                                                        <Select
                                                            value={assignment.job_id}
                                                            onValueChange={(value) => updateJobAssignment(index, 'job_id', value)}
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue placeholder={t('contracts.selectJob', 'people')} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {jobs.map((job) => (
                                                                    <SelectItem key={job.id} value={job.id}>
                                                                        {job.title}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div>
                                                        <Label className="text-xs">{t('contracts.level', 'people')}</Label>
                                                        <Select
                                                            key={`${index}-${formData.taxation_type}`}
                                                            value={assignment.job_level_id}
                                                            onValueChange={(value) => updateJobAssignment(index, 'job_level_id', value)}
                                                            disabled={!assignment.job_id}
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue placeholder={t('contracts.selectLevel', 'people')} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {availableLevels.map((level) => (
                                                                    <SelectItem key={level.id} value={level.id}>
                                                                        {level.level_name} ({formatCurrency(level.hourly_rate, getCurrencySymbol(baseCurrency))}/hr)
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div>
                                                        <Label className="text-xs">{t('contracts.costCenterOverride', 'people')}</Label>
                                                        <Select
                                                            value={assignment.cost_center_id || "none"}
                                                            onValueChange={(value) =>
                                                                updateJobAssignment(index, 'cost_center_id', value === "none" ? "" : value)
                                                            }
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue placeholder={t('contracts.useDefault', 'people')} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="none">{t('contracts.useDefault', 'people')}</SelectItem>
                                                                {costCenters.map((cc) => (
                                                                    <SelectItem key={cc.id} value={cc.id}>
                                                                        {cc.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeJobAssignment(index)}
                                                    className="mt-5"
                                                >
                                                    <TrashIcon className="w-4 h-4 text-red-500" />
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Dialog Footer */}
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateDialog(false);
                                    resetForm();
                                    setError(null);
                                }}
                            >
                                {t('actions.cancel', 'common')}
                            </Button>
                            <Button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? (editingContract ? t('actions.updating', 'common') : t('actions.creating', 'common')) : (editingContract ? t('actions.update', 'common') : t('actions.create', 'common'))}
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

export const Route = createFileRoute('/people/contracts')({
    validateSearch: contractsSearchSchema,
    component: ContractsPage,
})
